import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Upload, Trash2, Repeat2 } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { LivePreview } from "@/components/card/LivePreview";
import { PriceSummary } from "@/components/PriceSummary";
import { DesignPickerModal } from "@/components/design/DesignPickerModal";
import { Field, Section, TextInput } from "@/components/form/Fields";
import { AddressFields } from "@/components/form/AddressFields";
import { useCatalog } from "@/hooks/useCatalog";
import { useQuote } from "@/hooks/useQuote";
import { useOrder } from "@/state/OrderContext";
import { validateOrder } from "@/lib/validation";
import { uploadLogo } from "@/lib/api";
import {
  ALLOWED_LOGO_TYPES,
  FIELD_LIMITS,
  LOGO_CHOICES,
  MAX_LOGO_BYTES,
  QUANTITIES,
} from "@/lib/constants";
import { formatQty, formatRM } from "@/lib/formatters";

const LOGO_OPTIONS = [
  { value: LOGO_CHOICES.HAS, label: "Saya mempunyai logo" },
  { value: LOGO_CHOICES.NONE, label: "Saya tidak mahu menggunakan logo" },
  { value: LOGO_CHOICES.NOT_YET, label: "Saya belum mempunyai logo" },
];

const ProductOption = ({ product, price, selected, onSelect, gold }) => (
  <label
    data-testid={`radio-product-${product.code.toLowerCase()}`}
    className={`block cursor-pointer rounded-2xl border p-4 sm:p-5 transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-[1px] ${
      selected
        ? gold
          ? "border-[#c79a3b] bg-gradient-to-b from-[#fdf8ec] to-white shadow-[0_8px_22px_-16px_rgba(199,154,59,0.9)]"
          : "border-[#12876a] bg-gradient-to-b from-[#f2f8f5] to-white shadow-[0_8px_22px_-16px_rgba(18,135,106,0.9)]"
        : "border-[#0f3f2f]/12 bg-white/80"
    }`}
  >
    <input
      type="radio"
      name="productCode"
      value={product.code}
      checked={selected}
      onChange={() => onSelect(product.code)}
      data-testid={`radio-product-input-${product.code.toLowerCase()}`}
      className="sr-only"
    />
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className={`t-label ${gold ? "text-[#8a6a1f]" : ""}`}>{product.code}</p>
        <p className="mt-1 font-heading text-base font-bold text-[#14201b]">{product.name}</p>
      </div>
      <span
        className={`mt-1 grid place-items-center h-5 w-5 shrink-0 rounded-full border-2 ${
          selected ? "border-[#0f3f2f]" : "border-[#0f3f2f]/25"
        }`}
      >
        {selected && <span className="h-2.5 w-2.5 rounded-full bg-[#0f3f2f]" />}
      </span>
    </div>
    <ul className="mt-3 space-y-1 text-sm text-[#4a5a53]">
      <li>{product.material} {product.gsm}gsm</li>
      <li>{product.finishing}</li>
      <li>Cetakan warna penuh depan &amp; belakang</li>
    </ul>
    <p className="mt-3 text-sm font-semibold text-[#0f3f2f]">Dari {formatRM(price)}</p>
  </label>
);

export default function TempahPage() {
  const {
    order,
    patch,
    setBusiness,
    setBusinessAddress,
    setShippingAddress,
    setShippingSame,
    setQuantity,
    setProduct,
    setTemplate,
  } = useOrder();
  const { templateOf, productOf, priceOf, templates, products, loading } = useCatalog();
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);

  const template = templateOf(order.templateId);
  const productCode = order.productCode;
  const product = productOf(productCode);
  const { quote } = useQuote({
    productCode,
    quantity: order.quantity,
    country: order.shippingAddress.country,
    state: order.shippingAddress.state,
  });

  const b = order.business;
  const set = (k) => (e) => setBusiness({ [k]: e.target.value });

  const onLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      toast.error("Hanya fail PNG atau JPG dibenarkan.");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error("Saiz fail logo melebihi 3MB.");
      return;
    }
    setLogoBusy(true);
    try {
      const res = await uploadLogo(file);
      patch({
        logoPreview: `${process.env.REACT_APP_BACKEND_URL}${res.url}`,
        logoPath: res.storage_path,
        logoFileName: res.original_filename || file.name,
        logoChoice: LOGO_CHOICES.HAS,
      });
      toast.success("Logo berjaya dimuat naik.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Muat naik logo gagal. Sila cuba lagi.");
    } finally {
      setLogoBusy(false);
    }
  };

  const submit = () => {
    const errs = validateOrder(order);
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Sila lengkapkan maklumat yang diperlukan.");
      document.querySelector('[role="alert"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    navigate("/semak");
  };

  const previewStyle = template?.style_key || "minimalist-edge";

  const previewBlock = (idSuffix) => (
    <LivePreview
      styleKey={previewStyle}
      order={order}
      templateName={template?.name}
      testIdSuffix={idSuffix}
    />
  );

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-9 sm:py-12">
        <p className="t-label">Langkah 2 · Isi Maklumat</p>
        <h1 className="mt-2 t-h1">Maklumat Kad Perniagaan Anda</h1>
        <p className="mt-3 t-body max-w-2xl">
          Isi maklumat di bawah. Preview kad akan dikemas kini serta-merta.
        </p>

        {/* SELECTED DESIGN */}
        <div className="mt-6 flex flex-wrap items-center gap-3 rounded-2xl border border-[#0f3f2f]/12 bg-white/80 px-4 py-3">
          <span className="text-sm font-semibold text-[#14201b]" data-testid="chip-template">
            Design: {template ? `${template.template_id} — ${template.name}` : "Belum dipilih"}
          </span>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            data-testid="btn-tukar-design"
            className="btn-gold text-sm"
          >
            <Repeat2 strokeWidth={2} className="h-4 w-4" />
            Tukar Design
          </button>
        </div>
        {errors.templateId && (
          <p className="mt-2 text-sm text-[#b4242b]" role="alert">
            {errors.templateId}
          </p>
        )}

        <div className="mt-9 grid lg:grid-cols-12 gap-10">
          {/* MOBILE PREVIEW (normal flow, not sticky) */}
          <div className="lg:hidden">
            <div className="panel p-5">{previewBlock("-mobile")}</div>
          </div>

          <div className="lg:col-span-7 space-y-9">
            <Section
              letter="A"
              title="Maklumat Kad Perniagaan"
              description="Maklumat ini akan dicetak pada kad anda. Medan pilihan yang dibiarkan kosong tidak akan dicetak."
              testId="section-a"
            >
              <Field id="namaPenuh" label="Nama Penuh" error={errors.namaPenuh}>
                <TextInput id="namaPenuh" data-testid="input-nama-penuh" value={b.namaPenuh} onChange={set("namaPenuh")} error={errors.namaPenuh} />
              </Field>
              <Field id="jawatan" label="Jawatan" optional error={errors.jawatan}>
                <TextInput id="jawatan" data-testid="input-jawatan" value={b.jawatan} onChange={set("jawatan")} error={errors.jawatan} maxLength={FIELD_LIMITS.jawatan} />
              </Field>
              <Field id="namaPerniagaan" label="Nama Perniagaan / Syarikat" error={errors.namaPerniagaan}>
                <TextInput id="namaPerniagaan" data-testid="input-nama-perniagaan" value={b.namaPerniagaan} onChange={set("namaPerniagaan")} error={errors.namaPerniagaan} maxLength={FIELD_LIMITS.namaPerniagaan} />
              </Field>
              <Field
                id="bidangBisnes"
                label="Bidang Bisnes / Moto"
                optional
                error={errors.bidangBisnes}
                hint="Contoh: Pemborong dan Pengedar Barangan Runcit, Peguambela dan Peguamcara, atau Pelanggan Adalah Keutamaan Kami."
              >
                <TextInput
                  id="bidangBisnes"
                  data-testid="input-bidang-bisnes"
                  value={b.bidangBisnes}
                  onChange={set("bidangBisnes")}
                  error={errors.bidangBisnes}
                  maxLength={FIELD_LIMITS.bidangBisnes}
                />
              </Field>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field id="telefon" label="No. Telefon" error={errors.telefon}>
                  <TextInput id="telefon" data-testid="input-telefon" value={b.telefon} onChange={set("telefon")} error={errors.telefon} inputMode="tel" />
                </Field>
                <Field id="whatsapp" label="WhatsApp" optional error={errors.whatsapp}>
                  <TextInput id="whatsapp" data-testid="input-whatsapp" value={b.whatsapp} onChange={set("whatsapp")} error={errors.whatsapp} inputMode="tel" maxLength={FIELD_LIMITS.whatsapp} />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field id="email" label="Email" optional error={errors.email}>
                  <TextInput id="email" data-testid="input-email" value={b.email} onChange={set("email")} error={errors.email} type="email" />
                </Field>
                <Field id="website" label="Website" optional error={errors.website}>
                  <TextInput id="website" data-testid="input-website" value={b.website} onChange={set("website")} error={errors.website} maxLength={FIELD_LIMITS.website} />
                </Field>
              </div>
            </Section>

            <Section
              letter="B"
              title="Alamat Perniagaan"
              description="Alamat ini akan dicetak pada kad anda."
              testId="section-b"
            >
              <AddressFields
                prefix="ba"
                value={order.businessAddress}
                onChange={setBusinessAddress}
                errors={{
                  line1: errors.baLine1,
                  postcode: errors.baPostcode,
                  city: errors.baCity,
                  state: errors.baState,
                }}
              />
            </Section>

            <Section letter="C" title="Logo Perniagaan" description="Logo adalah pilihan." testId="section-c">
              <fieldset>
                <legend className="sr-only">Pilihan logo</legend>
                <div className="space-y-3">
                  {LOGO_OPTIONS.map((o) => (
                    <label
                      key={o.value}
                      className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer min-h-[48px] transition-colors duration-200 ${
                        order.logoChoice === o.value
                          ? "border-[#12876a] bg-[#f2f8f5]"
                          : "border-[#0f3f2f]/12 bg-white/80"
                      }`}
                    >
                      <input
                        type="radio"
                        name="logoChoice"
                        value={o.value}
                        checked={order.logoChoice === o.value}
                        onChange={() => patch({ logoChoice: o.value })}
                        data-testid={`radio-logo-${o.value}`}
                        className="h-5 w-5 accent-[#0f3f2f]"
                      />
                      <span className="text-sm font-medium text-[#14201b]">{o.label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {order.logoChoice === LOGO_CHOICES.HAS && (
                <div data-testid="logo-upload-area">
                  <Field id="logo-file" label="Muat Naik Logo" hint="PNG atau JPG. Maksimum 3MB. Fail disimpan dengan selamat untuk pemprosesan tempahan.">
                    <label className="flex items-center gap-3 rounded-xl border border-dashed border-[#0f3f2f]/25 bg-[#f7faf8] px-4 py-4 cursor-pointer hover:border-[#12876a] transition-colors duration-200">
                      <Upload strokeWidth={1.75} className="h-5 w-5 text-[#12876a]" />
                      <span className="text-sm text-[#4a5a53]">
                        {order.logoFileName || "Pilih fail logo"}
                      </span>
                      <input
                        id="logo-file"
                        type="file"
                        accept="image/png,image/jpeg"
                        onChange={onLogoFile}
                        data-testid="input-logo-file"
                        className="sr-only"
                      />
                    </label>
                  </Field>
                  {order.logoPreview && (
                    <button
                      type="button"
                      onClick={() => patch({ logoPreview: null, logoFileName: null })}
                      data-testid="btn-buang-logo"
                      className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#b4242b]"
                    >
                      <Trash2 strokeWidth={1.75} className="h-4 w-4" /> Buang logo
                    </button>
                  )}
                </div>
              )}

              {order.logoChoice === LOGO_CHOICES.NOT_YET && (
                <p
                  className="rounded-xl border border-[#c79a3b]/30 bg-[#fdf8ec] p-4 text-sm text-[#0f3f2f] leading-relaxed"
                  data-testid="msg-belum-ada-logo"
                >
                  Belum mempunyai logo? Tidak mengapa. Anda masih boleh membuat tempahan tanpa
                  logo. Design kad akan disesuaikan menggunakan nama perniagaan anda.
                </p>
              )}
            </Section>

            <Section letter="D" title="Alamat Penghantaran" description="Alamat untuk menghantar kad yang telah dicetak." testId="section-d">
              <label className="flex items-center gap-3 rounded-xl border border-[#0f3f2f]/12 bg-white/80 p-4 cursor-pointer min-h-[48px]">
                <input
                  type="checkbox"
                  checked={order.shippingSameAsBusiness}
                  onChange={(e) => setShippingSame(e.target.checked)}
                  data-testid="checkbox-shipping-same"
                  className="h-5 w-5 accent-[#0f3f2f]"
                />
                <span className="text-sm font-medium text-[#14201b]">
                  Alamat penghantaran sama dengan alamat perniagaan
                </span>
              </label>

              <AddressFields
                prefix="sa"
                value={order.shippingAddress}
                onChange={setShippingAddress}
                errors={{
                  line1: errors.saLine1,
                  postcode: errors.saPostcode,
                  city: errors.saCity,
                  state: errors.saState,
                }}
              />
            </Section>

            <Section
              letter="E"
              title="Jenis Kad & Kuantiti"
              description="Standard dan Premium adalah produk cetakan yang berbeza — bukan kualiti design. Semua 12 design tersedia untuk kedua-duanya."
              testId="section-e"
            >
              <fieldset>
                <legend className="block text-sm font-semibold text-[#14201b]">Jenis Kad</legend>
                <div className="mt-2.5 grid sm:grid-cols-2 gap-4">
                  {products.map((p) => (
                    <ProductOption
                      key={p.code}
                      product={p}
                      price={priceOf(p.code, 100)}
                      selected={productCode === p.code}
                      onSelect={setProduct}
                      gold={p.code === "PREMIUM"}
                    />
                  ))}
                </div>
                {errors.productCode && (
                  <p className="mt-2 text-sm text-[#b4242b]" role="alert" data-testid="error-productCode">
                    {errors.productCode}
                  </p>
                )}
              </fieldset>

              <fieldset>
                <legend className="block text-sm font-semibold text-[#14201b]">Kuantiti</legend>
                <div className="mt-2.5 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {QUANTITIES.map((q) => (
                    <label
                      key={q}
                      data-testid={`radio-qty-${q}`}
                      className={`rounded-xl border p-4 cursor-pointer min-h-[48px] transition-colors duration-200 ${
                        order.quantity === q
                          ? "border-[#12876a] bg-[#f2f8f5]"
                          : "border-[#0f3f2f]/12 bg-white/80"
                      }`}
                    >
                      <input
                        type="radio"
                        name="quantity"
                        checked={order.quantity === q}
                        onChange={() => setQuantity(q)}
                        data-testid={`radio-qty-input-${q}`}
                        className="sr-only"
                      />
                      <span className="block text-sm font-semibold text-[#14201b]">
                        {formatQty(q)}
                      </span>
                      <span className="block text-sm font-bold text-[#0f3f2f]">
                        {productCode ? formatRM(priceOf(productCode, q)) : "—"}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            </Section>

            <button type="button" onClick={submit} data-testid="btn-teruskan-semak" className="btn-primary w-full sm:w-auto">
              Semak Tempahan
              <ArrowRight strokeWidth={1.75} className="h-4 w-4" />
            </button>
          </div>

          {/* DESKTOP STICKY PREVIEW + PRICE */}
          <aside className="hidden lg:block lg:col-span-5 self-start sticky top-20">
            <div className="space-y-5">
              <div className="panel p-6">{previewBlock("")}</div>
              {!loading && (
                <PriceSummary
                  productName={product?.name}
                  quantity={order.quantity}
                  productAmount={quote?.product_amount}
                  shippingAmount={quote?.shipping_amount}
                  country={order.shippingAddress.country}
                  state={order.shippingAddress.state}
                  ready={Boolean(quote)}
                />
              )}
            </div>
          </aside>

          <div className="lg:hidden">
            {!loading && (
              <PriceSummary
                productName={product?.name}
                quantity={order.quantity}
                productAmount={quote?.product_amount}
                shippingAmount={quote?.shipping_amount}
                country={order.shippingAddress.country}
                state={order.shippingAddress.state}
                ready={Boolean(quote)}
                testId="price-summary-mobile"
              />
            )}
          </div>
        </div>
      </main>

      <DesignPickerModal
        open={pickerOpen}
        templates={templates}
        selectedId={order.templateId}
        onSelect={(id) => {
          setTemplate(id);
          setPickerOpen(false);
          toast.success("Design dikemas kini.");
        }}
        onClose={() => setPickerOpen(false)}
      />

      <Footer />
    </div>
  );
}
